import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "@/lib/bizday";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CalendarOff, Trash2 } from "lucide-react";
import { toast } from "sonner";

export function StaffScheduleButton({ staff }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`staff-schedule-btn-${staff.id}`} className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-md py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
        <CalendarOff className="h-3.5 w-3.5" /> Schedule & leave
      </button>
      {open && <StaffScheduleDialog staff={staff} onClose={() => setOpen(false)} />}
    </>
  );
}

const DAYS = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
const initRows = (wh) => Object.fromEntries(DAYS.map(([d]) => {
  const iv = wh?.[d]?.[0];
  return [d, { on: wh ? !!iv : true, start: iv?.[0] || "09:00", end: iv?.[1] || "18:00" }];
}));

export function WorkingHoursEditor({ staff, onSaved }) {
  const qc = useQueryClient();
  const [fixed, setFixed] = useState(!!staff.working_hours);
  const [rows, setRows] = useState(initRows(staff.working_hours));
  const [busy, setBusy] = useState(false);
  const setRow = (d, k, v) => setRows((r) => ({ ...r, [d]: { ...r[d], [k]: v } }));
  const save = async () => {
    setBusy(true);
    try {
      const wh = fixed ? Object.fromEntries(DAYS.map(([d]) => [d, rows[d].on ? [[rows[d].start, rows[d].end]] : []])) : null;
      await api.put(`/staff/${staff.id}/working-hours`, { working_hours: wh });
      qc.invalidateQueries({ queryKey: ["staff"] }); onSaved(); toast.success("Working hours saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="working-hours-editor">
      <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
        <input type="checkbox" checked={fixed} onChange={(e) => setFixed(e.target.checked)} data-testid="working-hours-fixed" />
        Fixed weekly working hours (unticked = no shift limits)
      </label>
      {fixed && (
        <div className="mt-2 space-y-1">
          {DAYS.map(([d, label]) => (
            <div key={d} className="flex items-center gap-2 text-xs" data-testid={`wh-row-${d}`}>
              <label className="flex w-20 items-center gap-1"><input type="checkbox" checked={rows[d].on} onChange={(e) => setRow(d, "on", e.target.checked)} data-testid={`wh-on-${d}`} />{label}</label>
              {rows[d].on ? (
                <>
                  <Input type="time" value={rows[d].start} onChange={(e) => setRow(d, "start", e.target.value)} className="h-8 w-28" data-testid={`wh-start-${d}`} />
                  <span>–</span>
                  <Input type="time" value={rows[d].end} onChange={(e) => setRow(d, "end", e.target.value)} className="h-8 w-28" data-testid={`wh-end-${d}`} />
                </>
              ) : <span className="text-slate-400">Day off</span>}
            </div>
          ))}
        </div>
      )}
      <Button size="sm" disabled={busy} onClick={save} className="mt-2 bg-slate-900 text-white" data-testid="working-hours-save">{busy ? "Saving…" : "Save working hours"}</Button>
    </div>
  );
}

function StaffScheduleDialog({ staff, onClose }) {
  const qc = useQueryClient();
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [from, setFrom] = useState(dayjs().format("YYYY-MM-DDT09:00"));
  const [to, setTo] = useState(dayjs().format("YYYY-MM-DDT18:00"));
  const [reason, setReason] = useState("");
  const day = useQuery({ queryKey: ["staff-day", staff.id, date], queryFn: async () => (await api.get(`/staff/${staff.id}/schedule?date=${date}`)).data });
  const leave = useQuery({ queryKey: ["staff-leave", staff.id], queryFn: async () => (await api.get(`/staff/${staff.id}/unavailability`)).data });
  const refresh = () => ["staff-day", "staff-leave", "staff-avail"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const add = async () => {
    try {
      await api.post(`/staff/${staff.id}/unavailability`, { start_time: dayjs(from).format("YYYY-MM-DDTHH:mm:ssZ"), end_time: dayjs(to).format("YYYY-MM-DDTHH:mm:ssZ"), kind: "leave", reason });
      setReason(""); refresh(); toast.success("Unavailability added");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { try { await api.delete(`/staff/${staff.id}/unavailability/${id}`); refresh(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="staff-schedule-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{staff.name} — schedule</DialogTitle><DialogDescription>Actual service blocks for the day, plus leave and unavailable hours.</DialogDescription></DialogHeader>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-44" data-testid="staff-schedule-date" />
        <div className="space-y-1" data-testid="staff-schedule-list">
          {(day.data?.items || []).length === 0 ? <div className="text-sm text-slate-400">Nothing scheduled.</div> : day.data.items.map((x, i) => (
            <div key={i} className={`rounded-md border px-2 py-1.5 text-xs ${x.kind === "unavailable" ? "border-rose-200 bg-rose-50" : x.overlaps ? "border-amber-300 bg-amber-50" : "border-slate-200"}`} data-testid={`staff-block-${i}`}>
              <b>{dayjs(x.start_time).format("h:mm A")}–{dayjs(x.end_time).format("h:mm A")}</b> · {x.service_name}{x.kind === "booking" ? " (whole booking, legacy)" : ""}{x.overlaps ? " · overlap" : ""}
            </div>
          ))}
        </div>
        <div className="mt-2 border-t border-slate-100 pt-3">
          <WorkingHoursEditor staff={staff} onSaved={refresh} />
        </div>
        <div className="mt-2 border-t border-slate-100 pt-3">
          <Label className="text-xs">Add leave / unavailable hours</Label>
          <div className="mt-1 grid grid-cols-2 gap-2">
            <Input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9" data-testid="leave-from" />
            <Input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} className="h-9" data-testid="leave-to" />
          </div>
          <div className="mt-2 flex gap-2"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (e.g. leave)" className="h-9" data-testid="leave-reason" /><Button onClick={add} className="bg-slate-900 text-white" data-testid="leave-add-btn">Add</Button></div>
          <div className="mt-2 space-y-1" data-testid="leave-list">
            {(leave.data || []).map((u) => (
              <div key={u.id} className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1 text-xs" data-testid={`leave-${u.id}`}>
                <span>{dayjs(u.start_time).format("D MMM h:mm A")} – {dayjs(u.end_time).format("D MMM h:mm A")} · {u.reason || u.kind}</span>
                <button onClick={() => del(u.id)} data-testid={`leave-del-${u.id}`} className="text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
